import { useEffect, useId, useRef } from 'react'
import type { Product, PurchaseType } from '../../types'
import { formatRupiah, getAvailablePurchaseTypes } from '../../types'

import { CUSTOMER_TYPE_LABELS } from './customerTypeLabels'

interface TypeSelectPopupProps {
  product: Product
  selectedType: PurchaseType | null
  onSelect: (type: PurchaseType) => void
  onClose: () => void
}

/**
 * Compact type-selection popup. Lists ONLY purchase types that have a
 * configured price (from the API product row — never derived), each with
 * its exact price. Selecting an option applies it immediately and closes.
 * Full keyboard support: focus moves in on open, ESC / overlay closes,
 * focus returns to the trigger button.
 */
export function TypeSelectPopup({ product, selectedType, onSelect, onClose }: TypeSelectPopupProps) {
  const availableTypes = getAvailablePurchaseTypes(product)
  const titleId = useId()
  const firstOptionRef = useRef<HTMLButtonElement>(null)
  const previousFocusRef = useRef<Element | null>(null)

  useEffect(() => {
    previousFocusRef.current = document.activeElement
    firstOptionRef.current?.focus()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus()
      }
    }
  }, [onClose])

  return (
    <div className="type-popup-overlay" onClick={onClose}>
      <div
        className="type-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="type-popup-title">
          Pilih Tipe Pembelian
        </h3>
        <p className="type-popup-product">{product.name}</p>
        <ul className="type-popup-options" role="list">
          {availableTypes.map((type, index) => {
            const price = product[
              type === 'PCS' ? 'pricePcs' : type === 'PACK' ? 'pricePack' : 'priceHalfPack'
            ]
            const isSelected = selectedType === type
            return (
              <li key={type}>
                <button
                  type="button"
                  ref={index === 0 ? firstOptionRef : undefined}
                  onClick={() => onSelect(type)}
                  className={`type-popup-option ${isSelected ? 'selected' : ''}`}
                  aria-pressed={isSelected}
                >
                  <span className="type-popup-option-check" aria-hidden="true">
                    {isSelected ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : null}
                  </span>
                  <span className="type-popup-option-label">{CUSTOMER_TYPE_LABELS[type]}</span>
                  <span className="type-popup-option-price">{price != null ? formatRupiah(price) : '-'}</span>
                </button>
              </li>
            )
          })}
        </ul>
        <button type="button" onClick={onClose} className="type-popup-cancel">
          Batal
        </button>
      </div>
    </div>
  )
}
